import { useEffect, useState, useSyncExternalStore } from "react";
import { pendingPage, type PageWidthHint } from "@/app/lazyPage";

/** How long the page being left stays on screen before the box replaces it. */
export const PAGE_LOADING_DELAY_MS = 200;

/**
 * The LOADING box's cue: `null` until a route has been waiting for its page
 * chunk for `delayMs`, then the width of the page that is coming.
 *
 * Why this exists instead of a Suspense fallback. A route change is a React
 * transition, and a transition keeps the page already on screen rather than
 * reveal a fallback — which is exactly what we want for the first fraction of a
 * second (the old page, not a blank pane) and exactly wrong for a slow network
 * (no sign anything is happening). A keyed boundary that DID show its fallback
 * fixed the second and broke the first: the old page vanished the instant a
 * link was clicked and a blank, then a box, stood in for it — the "jump on
 * every page". So the boundary stays un-keyed, the page chunk reports itself
 * through `pendingPage` (urgent, readable mid-transition), and this hook turns
 * "still waiting after 200ms" into a box. A page that is already prefetched
 * never reports at all.
 */
export function usePageLoadingBox(
  delayMs: number = PAGE_LOADING_DELAY_MS,
): PageWidthHint | null {
  const pending = useSyncExternalStore(
    pendingPage.subscribe,
    pendingPage.getSnapshot,
    () => null,
  );
  // Which wait the timer has matured for. Keyed by the wait itself, so a box
  // that was due for a page the user already left can never carry over to the
  // next one — and nothing is reset synchronously inside the effect.
  const [matured, setMatured] = useState<symbol | null>(null);
  const token = pending?.token;
  const width = pending?.width ?? null;

  useEffect(() => {
    if (!token) return;
    const id = window.setTimeout(() => setMatured(token), delayMs);
    return () => window.clearTimeout(id);
  }, [token, delayMs]);

  return token && matured === token ? width : null;
}
